import type { ReactNode } from 'react';

import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import { Box, Typography } from '@mui/material';

export interface EmptyStateProps {
  /** Short headline. */
  title?: string;
  /** Supporting description. */
  description?: string;
  /** Custom icon (defaults to an inbox glyph). */
  icon?: ReactNode;
  /** Optional call-to-action (e.g. a button). */
  action?: ReactNode;
}

/** Generic "nothing here yet" state, business-agnostic. */
export function EmptyState({
  title = 'Nothing here yet',
  description = 'There is no data to display.',
  icon,
  action,
}: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        py: 6,
        px: 3,
        minHeight: 240,
        color: 'text.secondary',
      }}
    >
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>
        {icon ?? <InboxRoundedIcon sx={{ fontSize: 48 }} />}
      </Box>
      <Typography variant="h6" color="text.primary">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ maxWidth: 420 }}>
        {description}
      </Typography>
      {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
    </Box>
  );
}
